import React, { useMemo } from 'react';
import { ExternalLink, Lightbulb } from 'lucide-react';
import { NamingResultRow, typeBadge } from './types';

interface Props {
  rows: NamingResultRow[];
  loading: boolean;
  emptyText: string;
}

function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

// The needs-attention queue, grouped by assignee (unassigned last). Each row:
// the title as written, why it was flagged, and a rewrite to copy.
export default function AttentionQueue({ rows, loading, emptyText }: Props) {
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; rows: NamingResultRow[] }>();
    for (const r of rows) {
      const key = r.assignee?.account_id ?? '__unassigned__';
      const name = r.assignee?.display_name ?? (r.assignee ? r.assignee.account_id : 'Unassigned');
      const g = map.get(key) ?? { name, rows: [] };
      g.rows.push(r);
      map.set(key, g);
    }
    return Array.from(map.entries())
      .map(([key, g]) => ({ key, ...g }))
      .sort((a, b) => (a.key === '__unassigned__' ? 1 : b.key === '__unassigned__' ? -1 : b.rows.length - a.rows.length || a.name.localeCompare(b.name)));
  }, [rows]);

  if (loading) {
    return (
      <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
        {[0, 1, 2].map((i) => (
          <div key={i} className="animate-pulse border-t border-gray-100 px-5 py-4 first:border-t-0 motion-reduce:animate-none">
            <div className="h-3 w-24 rounded bg-gray-100" />
            <div className="mt-2 h-4 w-3/4 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white px-5 py-14 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-200/70">{emptyText}</div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
      {groups.map((g) => (
        <section key={g.key} aria-label={g.name}>
          <div className="sticky top-0 z-10 flex items-center gap-3 border-t border-gray-100 bg-gray-50/95 px-5 py-2.5 backdrop-blur first:border-t-0">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-50 text-[0.6875rem] font-semibold text-blue-700 ring-1 ring-inset ring-blue-200">
              {initials(g.key === '__unassigned__' ? null : g.name)}
            </div>
            <div className="text-sm font-semibold text-gray-900">{g.name}</div>
            <div className="text-xs text-gray-500">{g.rows.length} {g.rows.length === 1 ? 'ticket' : 'tickets'}</div>
          </div>
          <ul className="divide-y divide-gray-100">
            {g.rows.map((r) => {
              const suggestion = r.findings.find((f) => f.suggestion)?.suggestion ?? null;
              return (
                <li key={r.issue_id} className="px-5 py-4 transition-colors hover:bg-blue-50/30 motion-reduce:transition-none">
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={r.jira_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hit-area-sm inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-200/60 transition-colors hover:bg-blue-100 active:scale-[0.98] motion-reduce:transition-none"
                    >
                      {r.issue_key}
                      <ExternalLink className="h-3 w-3 opacity-60" />
                    </a>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${typeBadge(r.issue_type)}`}>{r.issue_type ?? 'Unknown'}</span>
                    {r.status && <span className="text-[0.6875rem] text-gray-500">{r.status}</span>}
                  </div>
                  <div className="mt-1.5 text-sm font-medium leading-snug text-gray-900">{r.issue_name ?? <span className="italic text-gray-500">No title</span>}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.findings.map((f) => (
                      <span key={f.rule} className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[0.6875rem] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">{f.message}</span>
                    ))}
                  </div>
                  {suggestion && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-600">
                      <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                      <span>{suggestion}</span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
